/* eslint-disable unicorn/filename-case */
import { useCallback, useEffect, useState } from "react";
import { Output, safeParse } from "valibot";

import { generationModes } from "shared/constants";
import { noop, safeParseJSON, sleep } from "shared/functions";
import { useGlobalsContext } from "shared/hooks";
import { getIpLookup } from "shared/ipLookup";
import { VariablesRecordSchema } from "shared/schemas";
import { AppConfiguration, GenerationMode, Literals } from "shared/types";
import replaceVariablesInString from "shared/utils/replaceVariablesInString";
import { removePeriodsAfterURL } from "shared/utils/sourceUtils";

import { LiveAgentHandoffInfo, UrlUpdateMapSchema } from "./schemas";
import {
  artificialWordByWordDelay,
  chatEndedDueToInactivityMessage,
  tenantNotFoundMessage,
} from "./constants";
import { parseSseErrorMessage } from "./functions";

const additionalFields = ["integration", "user_form_info"];

function useConnectSSE(
  botId: string,
  config: AppConfiguration,
  supportEmail: string | null,
  chatId: string,
  variables: Output<typeof VariablesRecordSchema>,
  setGenerationMode: React.Dispatch<React.SetStateAction<GenerationMode>>,
  setChatData: React.Dispatch<React.SetStateAction<any[]>>,
  setHasChatEnded: React.Dispatch<React.SetStateAction<boolean>>,
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  setEventSource: React.Dispatch<React.SetStateAction<EventSource | null>>,
  copyTexts: Literals,
  setLiveAgentHandoffInfo: React.Dispatch<
    React.SetStateAction<Output<typeof LiveAgentHandoffInfo> | null>
  >,
  authorizationToken?: string,
  customChatId?: string,
) {
  const { getParentUrl } = useGlobalsContext();
  const [ipAddress, setIpAddress] = useState<string>();
  useEffect(() => {
    getIpLookup({})
      .then(data => setIpAddress(data?.ip))
      .catch(noop);
  }, []);
  const onErrorSSE = useCallback(
    (sse: EventSource) => {
      sse.close();
      setGenerationMode(null);
    },
    [setGenerationMode],
  );

  const updateListener = useCallback(
    ({ data }: { data: string }) => {
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        if (lastMessage?.sent) {
          chatArray.push(lastMessage, {
            message: replaceVariablesInString(data, variables),
            sent: false,
          });
        } else {
          chatArray.push({
            ...lastMessage,
            message: replaceVariablesInString(
              lastMessage?.message + data,
              variables,
            ),
            sent: false,
          });
        }
        return chatArray;
      });
    },
    [setChatData, variables],
  );

  const breakListener = useCallback(() => {
    setChatData(prevChatData => {
      const chatArray = [...prevChatData];
      chatArray.push({
        message: "",
        sent: false,
        is_intermediate: true,
      });
      return chatArray;
    });
  }, [setChatData]);

  const integrationUpdateListener = useCallback(
    ({ data }: { data: string }) => {
      let message: any;
      try {
        message = JSON.parse(data);
      } catch {
        message = { message: data };
      }
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        if (lastMessage?.sent) {
          chatArray.push(lastMessage, {
            message: message?.message,
            sent: false,
            additionalInfo: {
              chatMessageType: message?.chat_message_type,
              createdTicketInfo: message?.created_ticket_info,
            },
          });
        } else {
          chatArray.push({
            ...lastMessage,
            message: lastMessage?.message + message?.message,
            sent: false,
            additionalInfo: {
              ...lastMessage?.additionalInfo,
              chatMessageType: message?.chat_message_type,
              createdTicketInfo: message?.created_ticket_info,
            },
          });
        }
        return chatArray;
      });
    },
    [setChatData],
  );

  const connectSSE = useCallback(
    (
      msg: string,
      chatUserId?: string,
      questionId?: string,
      is_support_request?: boolean,
      integration_type?: string,
      integration_data?: { [key: string]: any },
      formData?: { [key: string]: any },
    ): Promise<EventSource> => {
      return new Promise(async (resolve, reject) => {
        if (!botId) {
          return;
        }

        setGenerationMode(generationModes.submitting);

        const parentUrlHref = await new Promise(_resolve =>
          getParentUrl(parentUrl =>
            _resolve(parentUrl ? parentUrl.href : window.location.href),
          ),
        );

        const params: {
          [key: string]: any;
        } = {
          data: JSON.stringify({
            input_text: msg,
            token: config.token,
            chat_id: chatId,
            ip_address: ipAddress,
            starter_question_id: questionId,
            chat_user_id: chatUserId,
            BaseOrigin: config.baseOrigin,
            page: parentUrlHref,
            is_support_request,
            integration_type,
            integration_data,
            site_auth_token: authorizationToken,
            form_data: formData,
            custom_chat_id: customChatId,
          }),
        };

        for (const key of Object.keys(params)) {
          if (!params[key] || typeof params[key] !== "string") {
            delete params[key];
          }
        }

        try {
          const sse = new EventSource(
            `${
              config.serviceBaseUrl
            }/v1/botsonic/generate/sse?${new URLSearchParams(
              params,
            ).toString()}`,
          );

          setChatData(prevChatData => {
            const chatArray = [...prevChatData];
            chatArray.push({
              message: msg,
              sent: true,
              submitted_form_field_data: formData,
            });
            return chatArray;
          });

          sse?.addEventListener("message_id", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();
              if (lastMessage?.sent) {
                chatArray.push(lastMessage, {
                  id: event.data,
                  message: lastMessage?.message,
                  sent: false,
                });
              } else {
                chatArray.push({
                  ...lastMessage,
                  id: event.data,
                });
              }
              return chatArray;
            });
          });

          sse?.addEventListener("image", function (event) {
            let image_urls = event.data;
            image_urls = image_urls.replaceAll("'", '"');
            image_urls = JSON.parse(image_urls);
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              chatArray.push({
                imageUrls: image_urls.image_urls,
                sent: false,
                message: "",
              });
              return chatArray;
            });
          });

          sse?.addEventListener("renderable_sources", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              let renderableSources;
              try {
                renderableSources = JSON.parse(event.data);
              } catch {
                return chatArray;
              }
              const lastMessage = chatArray.pop();
              chatArray.push({
                ...lastMessage,
                renderableSource: renderableSources[0],
                sent: false,
              });
              return chatArray;
            });
          });

          sse?.addEventListener("live_agent_info", function (event) {
            setLiveAgentHandoffInfo(JSON.parse(event.data));
          });

          sse?.addEventListener("integration_update", function (event) {
            integrationUpdateListener(event);
          });

          const processStarterQuestion = async (answer: string) => {
            setGenerationMode(generationModes.generating);

            const fakeEventSource = {
              open: true,
              close() {
                this.open = false;
              },
            };
            setEventSource(fakeEventSource as unknown as EventSource);

            const answerWords: string[] = answer.split(" ");
            const sendWord = async () => {
              const newWord = answerWords.shift();
              updateListener({
                data: `${newWord}${answerWords.length ? " " : ""}`,
              });
              return sleep(artificialWordByWordDelay);
            };

            while (answerWords.length && fakeEventSource.open) {
              await sendWord();
            }

            setGenerationMode(null);
            setEventSource(null);
          };

          sse?.addEventListener("update", (event: MessageEvent<any>) => {
            if (questionId) {
              return processStarterQuestion(event.data);
            }
            updateListener(event);
          });

          sse?.addEventListener("break", breakListener);

          sse?.addEventListener(
            "generated_url_to_correct_url_map",
            (event: MessageEvent<any>) => {
              const eventData = safeParseJSON(event.data);
              const result = safeParse(UrlUpdateMapSchema, eventData);
              if (!result.success) {
                /* eslint-disable no-console */
                console.error("Failed to parse UrlUpdateMapSchema");
                return console.error(result.issues);
                /* eslint-enable no-console */
              }
              setChatData(prevChatData => {
                for (const [bad, good] of Object.entries(result.output)) {
                  prevChatData.at(-1).message = prevChatData
                    .at(-1)
                    .message.replaceAll(bad, good);
                }
                return [...prevChatData];
              });
            },
          );

          sse?.addEventListener("end", function () {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();

              chatArray.push({
                ...lastMessage,
                message: removePeriodsAfterURL(lastMessage?.message),
                sent: false,
                is_intermediate: false,
              });
              return chatArray;
            });

            setGenerationMode(null);

            sse.close();
          });

          sse?.addEventListener("sources", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();
              let sources = [];
              try {
                sources = JSON.parse(event.data);
              } catch {
                sources = [];
              }
              chatArray.push({
                ...lastMessage,
                sent: false,
                sources,
              });
              return chatArray;
            });
          });

          sse?.addEventListener("follow_ups", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();
              let follow_ups = [];
              try {
                follow_ups = JSON.parse(event.data);
              } catch {
                //DO NOTHING
              }
              chatArray.push({
                ...lastMessage,
                sent: false,
                follow_ups,
              });
              return chatArray;
            });
          });

          const getAdditionalFieldListener =
            (key: (typeof additionalFields)[number]) =>
            (event: MessageEvent<any>) => {
              setChatData(prevChatData => {
                const chatArray = [...prevChatData];
                const lastMessage = chatArray.pop();
                chatArray.push({
                  ...lastMessage,
                  message: removePeriodsAfterURL(lastMessage?.message),
                  sent: false,
                  [key]: JSON.parse(event.data),
                });
                return chatArray;
              });
              setMessage("");
              setGenerationMode(null);
            };

          for (const additionalField of additionalFields) {
            sse?.addEventListener(
              additionalField,
              getAdditionalFieldListener(additionalField),
            );
          }

          sse?.addEventListener("error", function (event: any) {
            interface ErrorEventData {
              status_code: number;
              error_message: string;
            }
            let eventData: ErrorEventData;
            try {
              eventData = JSON.parse(event.data);
            } catch {
              eventData = {
                status_code: 500,
                error_message: "Internal Server Error",
              };
            }
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              let message = "";
              switch (eventData.status_code) {
                // Message from BE should be user's custom message for no sources
                case 400:
                  message = eventData.error_message;
                  break;
                case 403:
                  // Message from BE should always be chatEndedDueToInactivityMessage
                  if (
                    eventData.error_message === chatEndedDueToInactivityMessage
                  ) {
                    message = eventData.error_message;
                  }
                  break;
                case 404:
                  // Message from BE should be "Requested tenant not found"
                  if (eventData.error_message === tenantNotFoundMessage) {
                    message = copyTexts.tenantNotFound;
                  }
                  break;
                case 500:
                  message = copyTexts.serverError;
                  break;
                default:
                  message =
                    parseSseErrorMessage(event.data, config.baseOrigin) ||
                    copyTexts.errorMessage +
                      (supportEmail ? " at " + supportEmail : "") +
                      ".";
                  break;
              }

              chatArray.push({
                message,
                sent: false,
              });
              return chatArray;
            });

            if (
              eventData.status_code === 403 &&
              eventData.error_message === chatEndedDueToInactivityMessage
            ) {
              setHasChatEnded(true);
            }

            setGenerationMode(null);
            sse.close();
          });

          sse.addEventListener("error", () => onErrorSSE(sse));
          sse.addEventListener("open", () => resolve(sse));
        } catch (err) {
          reject(err);
        }
      });
    },
    [
      botId,
      setGenerationMode,
      config.token,
      config.baseOrigin,
      config.serviceBaseUrl,
      chatId,
      ipAddress,
      getParentUrl,
      setChatData,
      setHasChatEnded,
      setLiveAgentHandoffInfo,
      setEventSource,
      updateListener,
      breakListener,
      setMessage,
      copyTexts.serverError,
      copyTexts.errorMessage,
      copyTexts.tenantNotFound,
      supportEmail,
      onErrorSSE,
      authorizationToken,
      integrationUpdateListener,
      customChatId,
    ],
  );

  return {
    connectSSE,
  };
}

export default useConnectSSE;
